import React, { useEffect, useRef, useState } from "react";
import { getDDMM } from "@/lib/codegen";

/**
 * Inline single-line auto-fit text: starts at `maxFontSize` and shrinks until
 * the text fits the parent's width without wrapping.
 */
function AutoFitText({ text, maxFontSize = 36, minFontSize = 8, className = "", testid }) {
  const ref = useRef(null);
  const [size, setSize] = useState(maxFontSize);

  useEffect(() => {
    if (!ref.current || !ref.current.parentElement) return;
    const el = ref.current;
    const parent = el.parentElement;
    const fit = () => {
      const containerW = parent.clientWidth - 4; // small inner buffer
      let s = maxFontSize;
      el.style.fontSize = `${s}px`;
      while (el.scrollWidth > containerW && s > minFontSize) {
        s -= 1;
        el.style.fontSize = `${s}px`;
      }
      setSize(s);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [text, maxFontSize, minFontSize]);

  return (
    <span
      ref={ref}
      data-testid={testid}
      className={`whitespace-nowrap inline-block ${className}`}
      style={{ fontSize: `${size}px`, lineHeight: 1.05 }}
    >
      {text}
    </span>
  );
}

/**
 * Reusable Shipment Label preview. Mirrors the PDF output.
 */
export default function LabelPreview({ shipment, pageIndex = 1 }) {
  const isAuto = shipment.package_mode === "auto";
  const isManual = shipment.package_mode === "manual";
  const total = Math.max(1, Number(shipment.package_total) || 1);

  const autoPkgText = isAuto ? `${pageIndex}/${total}` : "";

  const ddmm = (() => {
    try {
      return shipment.created_at ? getDDMM(new Date(shipment.created_at)) : getDDMM();
    } catch {
      return getDDMM();
    }
  })();

  return (
    <div
      className="border-2 border-dashed border-zinc-300 bg-[#FEFCE8] rounded-lg overflow-hidden relative"
      data-testid="label-preview"
    >
      <div className="px-6 pt-5 flex items-center justify-between">
        <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-zinc-700">
          Shipment Label
        </span>
        <span className="text-[11px] font-mono text-zinc-500">
          {shipment.shipment_mode || "—"} · {ddmm}
        </span>
      </div>

      <div className="px-6 pt-2 pb-1">
        <div className="barcode-pattern" />
      </div>

      <div className="px-6 py-8 text-center min-h-[180px] flex flex-col items-center justify-center w-full">
        {shipment.generated_code ? (
          <>
            <div className="w-full font-mono font-bold tracking-tight text-zinc-900">
              <AutoFitText
                text={shipment.generated_code}
                maxFontSize={36}
                minFontSize={8}
                testid="preview-generated-code"
              />
            </div>

            {/* Auto mode: big "1/N" */}
            {isAuto && (
              <div className="w-full font-mono font-bold tracking-tight text-zinc-900 mt-3">
                <AutoFitText
                  text={autoPkgText}
                  maxFontSize={36}
                  minFontSize={10}
                  testid="preview-package-number"
                />
              </div>
            )}

            {/* Manual mode: "Pkg No :" at ~60% of the max code size */}
            {isManual && (
              <div className="w-full font-mono font-bold tracking-tight text-zinc-900 mt-3">
                <AutoFitText
                  text="Pkg No :"
                  maxFontSize={22}
                  minFontSize={10}
                  testid="preview-manual-package-block"
                />
              </div>
            )}
          </>
        ) : (
          <div className="text-zinc-500 italic">Fill in the form to generate your code…</div>
        )}
      </div>

      <div className="px-6 pb-2">
        <div className="barcode-pattern" />
      </div>

      <div className="px-6 pb-5 pt-2 flex justify-between items-end">
        <div
          className="text-[10px] font-semibold tracking-[0.12em] uppercase text-zinc-700 font-mono"
          data-testid="preview-en-no"
        >
          {shipment.en_no ? `EN NO: ${shipment.en_no}` : ""}
        </div>
        <div className="text-[9px] text-zinc-400 font-mono">
          {isAuto && total > 1 ? `${total} pages` : "1 page"}
        </div>
      </div>
    </div>
  );
}
